import Button from "@mui/material/Button";
import MuiLink from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { StatusChip } from "components/StatusChip";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { Link } from "react-router-dom";

import {
  expiryDisplay,
  formatDate,
  matchKindLabel,
  ReviewRow,
  violationDateRange,
} from "./reviewDisplay";

export function ReleasedByCell({ row }: { row: ReviewRow }) {
  if (!row.acceptedAt) {
    return null;
  }
  return (
    <Stack>
      <Typography variant="body2">{row.acceptedByName ?? "Encamp"}</Typography>
      <Typography variant="caption" color="text.secondary">
        {formatDate(row.acceptedAt)}
      </Typography>
    </Stack>
  );
}

/**
 * Facility over customer. A fleet-wide queue has to name the customer, but as
 * its own column it repeated one value down the page while the columns that
 * actually differ per row were truncated.
 */
export function FacilityCell({ row }: { row: ReviewRow }) {
  return (
    <Stack sx={{ minWidth: 0 }}>
      <MuiLink
        component={Link}
        to={`/o/${row.tenantId}/facilities/${row.facilityId}/profile`}
        target="_blank"
        variant="body2"
        onClick={(event) => event.stopPropagation()}
      >
        {row.facilityName}
      </MuiLink>
      <Typography variant="caption" color="text.secondary">
        {row.tenantName}
      </Typography>
    </Stack>
  );
}

/**
 * The matched value, what kind of match it was, and — when it applies — that the
 * same ID is still live at another site.
 *
 * "Live elsewhere" sits here rather than in its own column because it is a fact
 * *about this ID*, and as a column it was blank for almost every row while
 * costing width the truncated columns needed.
 */
export function MatchedIdCell({ row }: { row: ReviewRow }) {
  return (
    <Stack sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2">{row.altIdValue}</Typography>
        {row.activeElsewhere ? <StatusChip color="warning" label="Live elsewhere" /> : null}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {matchKindLabel(row.matchKind)}
      </Typography>
    </Stack>
  );
}

/**
 * The expiry behind the group, or an orphaned marker when the ID row was deleted
 * outright rather than expired — a state, so it reads as a chip rather than as
 * text pretending to be a date.
 */
export function ExpiredCell({ row }: { row: ReviewRow }) {
  if (row.alternateIds.length === 0) {
    return <StatusChip color="default" label="Orphaned — ID deleted" />;
  }
  return <Typography variant="body2">{expiryDisplay(row.alternateIds)}</Typography>;
}

/**
 * Accept carries the staff orange (`secondary`) per the design system: this is a
 * staff-only action, and a contained blue button would read as an ordinary
 * customer-facing primary. Un-accept is the reversal, so it takes the text
 * treatment the system points secondary actions toward.
 */
export function DecisionCell({
  row,
  decide,
  saving,
}: {
  row: ReviewRow;
  decide: (row: ReviewRow, accepted: boolean) => void;
  saving: boolean;
}) {
  const accepted = Boolean(row.acceptedAt);
  return (
    <Button
      size="small"
      variant={accepted ? "text" : "contained"}
      color="secondary"
      disabled={saving}
      // The row opens the drawer; deciding from the row must not also open it.
      onClick={(event) => {
        event.stopPropagation();
        decide(row, !accepted);
      }}
    >
      {accepted ? "Un-accept" : "Accept"}
    </Button>
  );
}

/**
 * Only the columns whose values the database can order by are sortable — the
 * rest are derived per row after the page is fetched.
 */
export function buildViolationMatchColumns(
  decide: (row: ReviewRow, accepted: boolean) => void,
  saving: boolean
): OmnisearchGridColDef<ReviewRow>[] {
  return [
    {
      field: "facilityName",
      headerName: "Facility",
      flex: 0.9,
      minWidth: 170,
      renderCell: ({ row }) => <FacilityCell row={row} />,
    },
    {
      field: "altIdValue",
      headerName: "Matched ID",
      flex: 0.8,
      minWidth: 170,
      sortable: false,
      renderCell: ({ row }) => <MatchedIdCell row={row} />,
    },
    {
      field: "alternateIds",
      headerName: "Expired",
      flex: 0.5,
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => <ExpiredCell row={row} />,
    },
    {
      field: "violationCount",
      headerName: "Held",
      flex: 0.3,
      minWidth: 70,
      sortable: false,
    },
    {
      field: "violationDates",
      headerName: "Violation dates",
      flex: 1.2,
      minWidth: 150,
      sortable: false,
      valueGetter: (_, row) => violationDateRange(row),
    },
    {
      field: "firstSeenAt",
      headerName: "First seen",
      flex: 0.4,
      minWidth: 115,
      valueGetter: (_, row) => formatDate(row.firstSeenAt),
    },
    {
      // Empty in the default "Awaiting review" view; the reason the accepted
      // rows are worth showing at all is that they name who released a group.
      field: "acceptedAt",
      headerName: "Released by",
      flex: 0.6,
      minWidth: 140,
      renderCell: ({ row }) => <ReleasedByCell row={row} />,
    },
    {
      field: "decision",
      headerName: "",
      // Fixed, not flex: a flexed action column shrinks under width pressure
      // until the button is clipped mid-word.
      width: 130,
      sortable: false,
      align: "right",
      renderCell: ({ row }) => <DecisionCell row={row} decide={decide} saving={saving} />,
    },
  ];
}

/**
 * Every column field, so the omnisearch filter popover can be suppressed
 * wholesale: the resolver treats `search` as plain free text, so any `key:value`
 * token the popover produced would match nothing and read as an empty backlog.
 */
export const VIOLATION_MATCH_COLUMN_FIELDS = [
  "facilityName",
  "altIdValue",
  "alternateIds",
  "violationCount",
  "violationDates",
  "firstSeenAt",
  "acceptedAt",
  "decision",
];
